import type { InvestigationReadLead } from '@/domains/types'

/** Average adult silent-reading speed in words/minute — the figure Medium's
 * reading-time estimator popularized and most reading-time libraries default
 * to (e.g. npm's `reading-time`). */
const WORDS_PER_MINUTE = 200

/** Strips markdown syntax and HTML down to plain words, mirroring
 * `summarize`'s stripping in `useDocumentMeta` — good enough for a word
 * count, not meant to produce readable prose. */
function countWords(value: string | null | undefined): number {
  if (!value) return 0

  const text = value
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[*_`~>#]/g, '')
    .trim()

  if (!text) return 0

  return text.split(/\s+/).length
}

/**
 * Estimated reading time (in whole minutes, minimum 1) for an investigation
 * article: its own description/notes plus every lead's and fact's
 * description/notes — i.e. every markdown field `ReadArticleHeader` and
 * `ReadLeadSection`/`ReadFactBlock` actually render.
 */
export function estimateReadingMinutes(
  investigation: { description?: string | null; notes?: string | null },
  leads: InvestigationReadLead[],
): number {
  let words = countWords(investigation.description) + countWords(investigation.notes)

  for (const { lead, facts } of leads) {
    words += countWords(lead.description) + countWords(lead.notes)
    for (const fact of facts) {
      words += countWords(fact.description)
    }
  }

  return Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))
}
